// Hub analytics. Two separate jobs, never mixed:
//
// 1. GA4 events (behaviour): track(name, params) sends a gtag event ONLY when the visitor has accepted analytics
//    cookies. The store's Google tag (Google & YouTube channel) puts gtag on the page, but Shopify's consent API is
//    not loaded by default, so we load it on demand (Shopify.loadFeatures consent-tracking-api) and ask it.
//    No consent, no gtag, or demo mode -> the call is a no-op.
//
// 2. First-party credit (commerce): hubTouch() writes _mcc_hub_* cart attributes when the hub adds to the basket or
//    a shopper follows a product link out of a hub page. They ride onto the order's note_attributes, where the
//    analytics bridge reads them (finance_extract _parse_hub) to credit the order to the hub page it came from.
//    Same pattern as the article tracker's _mcc_first_*/_mcc_last_* (evidence, not trusted data). The first touch is
//    read back from the cart itself, so nothing is stored on the device.
import { config } from './singles-hub--config.js?v=c7c57de095';

const CUR = 'GBP';
const clip = (v, n) => (v == null ? v : String(v).slice(0, n));

// GA4 limits: 40-char names, 100-char string values, 25 params. Drop empties so reports don't fill with "(not set)".
export function cleanParams(p) {
  const out = {};
  Object.keys(p || {}).slice(0, 24).forEach((k) => {
    const v = p[k];
    if (v === undefined || v === null || v === '' || (typeof v === 'number' && !isFinite(v))) return;
    out[clip(k, 40)] = typeof v === 'string' ? clip(v, 100) : typeof v === 'boolean' ? String(v) : v;
  });
  return out;
}

// Which hub screen the visitor is on: the first mounted app's kind (hub | builder | search | archetype | list | ...).
export function surface(doc) {
  try {
    const d = doc || document;
    const kinds = Array.from(d.querySelectorAll('[data-mcc-kind]')).map((el) => el.getAttribute('data-mcc-kind'));
    // a deck page mounts the hero and the page body; the body names the screen
    return kinds.find((k) => k !== 'archetype-hero') || kinds[0] || 'page';
  } catch (e) { return 'page'; }
}

// ------------------------------------------------------------------ consent
let consentLoad = null;
function privacyApi() {
  const s = typeof window !== 'undefined' && window.Shopify;
  return s && s.customerPrivacy && typeof s.customerPrivacy.analyticsProcessingAllowed === 'function' ? s.customerPrivacy : null;
}
function loadPrivacy() {
  if (privacyApi()) return Promise.resolve(true);
  if (consentLoad) return consentLoad;
  consentLoad = new Promise((resolve) => {
    try {
      const s = window.Shopify;
      if (!s || typeof s.loadFeatures !== 'function') { resolve(false); return; }
      s.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) => resolve(!err && !!privacyApi()));
      setTimeout(() => resolve(!!privacyApi()), 5000);
    } catch (e) { resolve(false); }
  });
  return consentLoad;
}
export async function analyticsAllowed() {
  if (!(await loadPrivacy())) return false;
  try { return !!privacyApi().analyticsProcessingAllowed(); } catch (e) { return false; }
}

// ------------------------------------------------------------------ GA4 events
export function track(name, params) {
  try {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    if ((window.MCC_CONFIG || {}).cart === 'demo') return;
    const p = cleanParams(Object.assign({ hub_surface: surface() }, params || {}));
    analyticsAllowed().then((ok) => { if (ok) { try { window.gtag('event', name, p); } catch (e) { /* never break the hub */ } } });
  } catch (e) { /* never break the hub */ }
}

// Search fires on every keystroke-driven run: send only the query the visitor settled on (GA4's recommended event).
let searchTimer = null; let lastSearch = '';
export function trackSearch(term, results, filters) {
  const t = String(term || '').trim().toLowerCase();
  clearTimeout(searchTimer);
  if (t.length < 2 && !filters) return;
  searchTimer = setTimeout(() => {
    const key = t + '|' + (filters || '');
    if (key === lastSearch) return;
    lastSearch = key;
    track('search', { search_term: t, search_source: 'singles_hub', results, filters });
  }, 1500);
}

export const money = (lines) => Math.round(lines.reduce((a, l) => a + (Number(l.price) || 0) * (l.qty || 0), 0) * 100) / 100;
export const GA_CURRENCY = CUR;

// ------------------------------------------------------------------ first-party credit
// Pure: the cart attributes for one hub touch. currentAttrs = the cart's attributes now (first touch is kept).
export function buildHubAttributes(page, surf, action, nowIso, currentAttrs) {
  const a = { _mcc_hub_last_page: clip(page, 120), _mcc_hub_last_surface: clip(surf, 40), _mcc_hub_last_action: clip(action, 40), _mcc_hub_last_at: nowIso };
  const cur = currentAttrs || {};
  if (!cur._mcc_hub_first_page) {
    a._mcc_hub_first_page = a._mcc_hub_last_page;
    a._mcc_hub_first_surface = a._mcc_hub_last_surface;
    a._mcc_hub_first_at = nowIso;
  }
  return a;
}

// Write the touch. keepalive so a product-link click's navigation doesn't cancel it. Resolves (never rejects).
export function hubTouch(action, currentAttrs) {
  try {
    if (typeof window === 'undefined') return Promise.resolve(null);
    const attrs = buildHubAttributes(location.pathname, surface(), action, new Date().toISOString(), currentAttrs);
    const url = (config().routes && config().routes.cartUpdate) || '/cart/update.js';
    return fetch(url, {
      method: 'POST', keepalive: true, credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attrs }),
    }).then((r) => (r.ok ? r.json().catch(() => null) : null)).catch(() => null);
  } catch (e) { return Promise.resolve(null); }
}

// Product links out of any hub mount (card sheet "view product", search results, deck pages): credit the hub page.
export function bindProductLinkTouch(getAttrs) {
  if (typeof document === 'undefined' || document.__mccHubLinks) return;
  document.__mccHubLinks = true;
  document.addEventListener('click', (e) => {
    const a = e.target && e.target.closest && e.target.closest('a[href*="/products/"]');
    if (!a || !a.closest('[data-mcc-app], [data-mcc-host]')) return;
    hubTouch('product_link', getAttrs());
    track('hub_product_link', { product: (/\/products\/([^/?#]+)/.exec(a.getAttribute('href') || '') || [])[1] });
  }, true);
}
